// src/components/property/video-settings/UserTemplateForm.tsx
/**
 * UserTemplateForm Component
 * Form for creating/editing user templates
 *
 * Feature: Video Settings
 */

import { useReducer, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import type {
  IUserTemplateFormProps,
  TemplateCategory,
  IUserTemplateFormData,
} from '@/@types/videoSettings';

const CATEGORIES: { key: TemplateCategory; label: string }[] = [
  { key: 'building', label: 'Building' },
  { key: 'outdoor', label: 'Outdoor' },
  { key: 'interior', label: 'Interior' },
  { key: 'other', label: 'Other' },
];

type TemplateFormState = {
  name: string;
  positivePrompt: string;
  negativePrompt: string;
  categories: TemplateCategory[];
  error: string | null;
};

type TemplateFormAction =
  | { type: 'SET_NAME'; payload: string }
  | { type: 'SET_POSITIVE_PROMPT'; payload: string }
  | { type: 'SET_NEGATIVE_PROMPT'; payload: string }
  | { type: 'TOGGLE_CATEGORY'; payload: { category: TemplateCategory; checked: boolean } }
  | { type: 'SET_ERROR'; payload: string | null };

function templateFormReducer(
  state: TemplateFormState,
  action: TemplateFormAction
): TemplateFormState {
  switch (action.type) {
    case 'SET_NAME':
      return { ...state, name: action.payload };
    case 'SET_POSITIVE_PROMPT':
      return { ...state, positivePrompt: action.payload };
    case 'SET_NEGATIVE_PROMPT':
      return { ...state, negativePrompt: action.payload };
    case 'TOGGLE_CATEGORY':
      return {
        ...state,
        categories: action.payload.checked
          ? [...state.categories, action.payload.category]
          : state.categories.filter(c => c !== action.payload.category),
      };
    case 'SET_ERROR':
      return { ...state, error: action.payload };
  }
}

function UserTemplateForm({
  initialData,
  onSubmit,
  onCancel,
  isSubmitting,
}: IUserTemplateFormProps) {
  const [form, dispatch] = useReducer(templateFormReducer, {
    name: initialData?.name ?? '',
    positivePrompt: initialData?.positivePrompt ?? '',
    negativePrompt: initialData?.negativePrompt ?? '',
    categories: initialData?.categories ?? [],
    error: null,
  });

  const handleCategoryToggle = useCallback((category: TemplateCategory, checked: boolean) => {
    dispatch({ type: 'TOGGLE_CATEGORY', payload: { category, checked } });
  }, []);

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      dispatch({ type: 'SET_ERROR', payload: null });

      if (!form.name.trim()) {
        dispatch({ type: 'SET_ERROR', payload: 'Name is required' });
        return;
      }
      if (!form.positivePrompt.trim()) {
        dispatch({ type: 'SET_ERROR', payload: 'Positive prompt is required' });
        return;
      }
      if (form.categories.length === 0) {
        dispatch({ type: 'SET_ERROR', payload: 'At least one category must be selected' });
        return;
      }

      const data: IUserTemplateFormData = {
        name: form.name.trim(),
        positivePrompt: form.positivePrompt.trim(),
        negativePrompt: form.negativePrompt.trim(),
        categories: form.categories,
      };

      try {
        await onSubmit(data);
      } catch (err) {
        dispatch({
          type: 'SET_ERROR',
          payload: err instanceof Error ? err.message : 'Failed to save template',
        });
      }
    },
    [form.name, form.positivePrompt, form.negativePrompt, form.categories, onSubmit]
  );

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="space-y-2">
        <Label htmlFor="name">Template Name</Label>
        <Input
          id="name"
          value={form.name}
          onChange={e => dispatch({ type: 'SET_NAME', payload: e.target.value })}
          placeholder="My Custom Template"
          disabled={isSubmitting}
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="positivePrompt">Positive Prompt</Label>
        <Textarea
          id="positivePrompt"
          value={form.positivePrompt}
          onChange={e => dispatch({ type: 'SET_POSITIVE_PROMPT', payload: e.target.value })}
          placeholder="Describe the desired video style and motion..."
          rows={4}
          disabled={isSubmitting}
        />
        <p className="text-xs text-gray-500">
          Describe what the video should look like and how the camera should move.
        </p>
      </div>

      <div className="space-y-2">
        <Label htmlFor="negativePrompt">Negative Prompt</Label>
        <Textarea
          id="negativePrompt"
          value={form.negativePrompt}
          onChange={e => dispatch({ type: 'SET_NEGATIVE_PROMPT', payload: e.target.value })}
          placeholder="Describe what to avoid..."
          rows={3}
          disabled={isSubmitting}
        />
        <p className="text-xs text-gray-500">
          Describe what the video should NOT contain (artifacts, distortions, etc.)
        </p>
      </div>

      <div className="space-y-2">
        <Label>Categories</Label>
        <p className="text-xs text-gray-500 mb-2">
          Select which scene types this template can be used for.
        </p>
        <div className="flex flex-wrap gap-4">
          {CATEGORIES.map(cat => (
            <div key={cat.key} className="flex items-center gap-2">
              <Checkbox
                id={`category-${cat.key}`}
                checked={form.categories.includes(cat.key)}
                onCheckedChange={checked => handleCategoryToggle(cat.key, !!checked)}
                disabled={isSubmitting}
              />
              <Label htmlFor={`category-${cat.key}`} className="text-sm font-normal cursor-pointer">
                {cat.label}
              </Label>
            </div>
          ))}
        </div>
      </div>

      {form.error && (
        <div className="text-sm text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-950 p-3 rounded-md">
          {form.error}
        </div>
      )}

      <div className="flex gap-3 justify-end pt-2">
        <Button type="button" variant="outline" onClick={onCancel} disabled={isSubmitting}>
          Cancel
        </Button>
        <Button type="submit" disabled={isSubmitting}>
          {isSubmitting ? 'Saving...' : initialData ? 'Update Template' : 'Create Template'}
        </Button>
      </div>
    </form>
  );
}

export { UserTemplateForm };
